import React from 'react';
import { useState } from 'react';
import {
    Icon,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    MenuList,
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    TextField,
    Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import MaasCluster from './maas/form';
import AzureCluster from './azure/form';
import GcpCluster from './gcp/form';
import AwsCluster from './aws/form';
import OracleCluster from './oracle/form';
import IbmCluster from './ibm/form';
import * as Actions from './store/actions';
import * as ActionsDialog from 'app/store/actions';
import * as ClusterActions from '../../main/cluster/store/actions'
import * as RecordingActions from 'app/main/trainings/store/actions/recordings.actions';
import DeleteClusterWithRecordingsDialog from './components/common/DeleteClusterWithRecordingsDialog';
import * as CredentialActions from 'app/main/credentials/store/actions';
import * as SettingsActions from '../settings/store/actions';
import Logs from './Logs';
import ClusterTagsInput from './components/ClusterTagsInput';
import Error from 'app/fuse-layouts/shared-components/Error';
import Collaborators from '../../fuse-layouts/shared-components/Collaborators';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { CLUSTER_STATES, PROVIDERS, FSR_ACTIONS, FSR_STATE } from '../util/clusterUtils';
import { CLUSTER_FORM_TYPE } from './util';
import { USER_ROLE } from '../util/enums';

const useStyles = makeStyles(theme => ({
    modalHeader: {
        backgroundColor: theme.palette.secondary.main
    },
    uploadInput: {
        display: 'none'
    },
    menuPaper: {
        minWidth: '250px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: `1px solid ${theme.palette.divider}`,
    },
    darkMenuPaper: {
        minWidth: '250px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        border: `1px solid rgba(255, 255, 255, 0.12)`,
        backgroundColor: '#424242',
        color: '#ffffff',
        '& .MuiMenuItem-root': {
            color: '#ffffff',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
            },
            '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
            }
        },
        '& .MuiListItemIcon-root': {
            color: '#ffffff',
            minWidth: '40px',
        },
        '& .MuiListItemText-primary': {
            color: '#ffffff',
        }
    }
}));

const DuplicateClusterDialog = ({ cluster, onSave, onClose }) => {
    const dispatch = useDispatch();
    const credentials = useSelector(({ credentials }) => credentials.credential.list);
    const [name, setName] = useState('');
    const [credentialId, setCredentialId] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const filteredCredentials = credentials.filter(c => c.provider === cluster.provider);

    // Once credentials are loaded, default to the original credential if the user has access to it
    React.useEffect(() => {
        if (credentials.length === 0) {
            dispatch(CredentialActions.getCredentials());
        }
    }, [dispatch, credentials.length]);

    React.useEffect(() => {
        if (cluster.credential_id && filteredCredentials.length > 0) {
            const hasAccess = filteredCredentials.some(c => c.id === cluster.credential_id);
            setCredentialId(hasAccess ? cluster.credential_id : '');
        }
    }, [cluster.credential_id, filteredCredentials.length]);

    const validate = (value) => {
        if (!value.trim()) return 'Name is required';
        if (/[A-Z]/.test(value)) return 'Name cannot contain capital letters';
        if (/\s/.test(value)) return 'Name cannot contain spaces';
        return '';
    };

    const handleChange = (e) => {
        const val = e.target.value;
        setName(val);
        if (error) setError(validate(val));
    };

    const handleSave = async () => {
        const validationError = validate(name);
        if (validationError) {
            setError(validationError);
            return;
        }
        setSaving(true);
        const result = await onSave({ ...cluster, name, credential_id: credentialId || null });
        if (result && result.error) {
            setError(result.error);
            setSaving(false);
        }
    };

    return (
        <React.Fragment>
            <DialogTitle>Duplicate Cluster</DialogTitle>
            <DialogContent>
                <TextField
                    id="name"
                    name="name"
                    label="Name"
                    type="text"
                    variant="outlined"
                    value={name}
                    onChange={handleChange}
                    error={!!error}
                    helperText={error}
                    fullWidth
                    required
                    autoFocus
                    placeholder="Your Cluster Name (No Spaces or Capitals)"
                />
                <TextField
                    select
                    label="Credential"
                    variant="outlined"
                    value={credentialId}
                    onChange={(e) => setCredentialId(e.target.value)}
                    fullWidth
                    style={{ marginTop: 16 }}
                    helperText={!credentialId ? 'You can set a credential later from the cluster settings' : ''}
                >
                    <MenuItem value="">None (set later)</MenuItem>
                    {filteredCredentials.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    disabled={saving || !name.trim()}
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </React.Fragment>
    );
};

const ManageTagsDialog = ({ cluster, onClose }) => {
    const dispatch = useDispatch();
    const [tags, setTags] = useState(cluster.tags || []);
    const [saving, setSaving] = useState(false);

    var handleSave = function() {
        setSaving(true);
        dispatch(ClusterActions.updateClusterTags(cluster.id, tags))
            .then(function() {
                setSaving(false);
                onClose();
            })
            .catch(function() {
                setSaving(false);
            });
    };

    return (
        <React.Fragment>
            <DialogTitle>Manage Tags</DialogTitle>
            <DialogContent>
                <DialogContentText style={{ marginBottom: '12px' }}>
                    Edit tags for cluster <strong>{cluster.displayName || cluster.name}</strong>
                </DialogContentText>
                <ClusterTagsInput
                    tags={tags}
                    onChange={setTags}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">Cancel</Button>
                <Button onClick={handleSave} color="primary" disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </React.Fragment>
    );
};

export const getClusterActionConfirmationMessage = (clusterState, action) => {
    switch (action) {
        case 'delete':
            if (clusterState !== CLUSTER_STATES.DESTROYED) return 'This removes the cluster from Heidi but not from the provider account, which may leave abandoned resources. Destroy the cluster first to avoid this.';
            return 'This will permanently remove the cluster from Heidi. This cannot be undone.';
        case 'destroy':
        case 'stopping':
            if (clusterState !== CLUSTER_STATES.DESTROYED) return 'Make sure you back up any files before proceeding. You will lose all files in File Manager once cluster is destroyed. Go ahead and destroy?';
            return `This cannot be undone, Job outputs in File Manager will also be deleted (WIP). Are you sure you want to ${action} this cluster?`;
        case 'recycle':
            return 'This will destroy the cluster and automatically redeploy it with the same configuration. Make sure you back up any files before proceeding. You will lose all files in File Manager. Continue with recycling?';
        case 'resetState':
            return 'This action will reset the state of the cluster and may leave orphan artifacts that must be manually destroyed. Are you sure you want to proceed?';
        default:
            return `Are you sure you want to ${action} this cluster?`;
    }
}

const DestroyConfirmContent = ({ clusterName, clusterDisplayName, action, message, onConfirm, onCancel }) => {
    const [confirmText, setConfirmText] = useState('');
    const label = action === 'recycle' ? 'Recycle' : action === 'delete' ? 'Delete' : 'Destroy';
    const confirmWord = label.toLowerCase();
    const matches = confirmText.toLowerCase() === confirmWord;

    return (
        <React.Fragment>
            <DialogTitle>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon style={{ color: '#f57c00', fontSize: '28px' }}>warning</Icon>
                    <span style={{ color: '#d32f2f', fontSize: '22px' }}>{label} Cluster</span>
                </div>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <DialogContentText style={{ marginBottom: '12px', color: '#212121' }}>
                    You are about to <strong>{label.toLowerCase()}</strong> cluster <strong>"{clusterDisplayName || clusterName}"</strong>.
                </DialogContentText>
                <DialogContentText style={{ marginBottom: '16px', color: '#212121' }}>
                    {message}
                </DialogContentText>
                <DialogContentText style={{ color: '#666', marginBottom: '8px' }}>
                    To confirm, type <strong>{confirmWord}</strong> below:
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    label={`Type "${confirmWord}" to confirm`}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && matches) { e.preventDefault(); onConfirm(); } }}
                    placeholder={confirmWord}
                    helperText={confirmText && !matches ? `Please type "${confirmWord}" to confirm` : ''}
                    error={!!(confirmText && !matches)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    id="clusters_table_confirmation-back-btn"
                    style={{ fontSize: '18px', padding: '10px 15px' }}
                    onClick={onCancel}
                    color="primary"
                >
                    Back
                </Button>
                <Button
                    style={{ fontSize: '18px', padding: '10px 15px' }}
                    id="clusters_table_confirmation-confirm-btn"
                    onClick={onConfirm}
                    color="secondary"
                    variant="contained"
                    disabled={!matches}
                >
                    {label}
                </Button>
            </DialogActions>
        </React.Fragment>
    );
};

export const ClusterActionMenu = props => {
    const classes = useStyles(props);
    const reduxSelectedCluster = useSelector(({ clusters }) => clusters.cluster.selected);
    const selectedCluster = props.cluster || reduxSelectedCluster;
    const licenseType = useSelector(({ licenseKey }) => licenseKey.key.licenseType);
    const role = useSelector(({ auth }) => auth.user.role);
    const userId = useSelector(({ auth }) => auth.user.id);
    const dispatch = useDispatch();
    const [data, setData] = useState(null);

    // Use dark theme if prop is passed
    const menuPaperClass = props.darkTheme ? classes.darkMenuPaper : classes.menuPaper;

    const isUserOwner = (ownerId) => {
      return userId === ownerId;
    }
    
    const closeSelectedMenu = () => {
        if (props.onClose) {
            props.onClose();
        }
        if (props.setSelectedMenu) {
            props.setSelectedMenu(null);
        }
        // Only clear Redux selection if we're not using the cluster prop
        if (!props.cluster) {
            setTimeout(() => {
                dispatch(Actions.setSelected(null));
            }, 300);
        }
    };
    const handleClick = (cluster, targetHash = 'access') => {
        if (cluster.info.state === 'available') {
            const data = {
                id: cluster.id
            };
            if (targetHash === 'cluster-info') {
                dispatch(Actions.getClusterActivities(data));
            }

            dispatch(Actions.getCluster(data)).then(() => props.history.push({ pathname: `/clusters/${cluster.id}`, hash: targetHash }));
        }
        closeSelectedMenu();
    };
    const handleDeploy = id => {
        deployConfirm(id);
    };
    const handleDuplicateCluster = (cluster) => {
        closeSelectedMenu();
        const onSave = async (newCluster) => {
            try {
                const response = await dispatch(ClusterActions.saveCluster(newCluster));
                if (response.status === 400) {
                    return { error: response.data.message };
                } else if (response.status !== 200) {
                    return { error: `Server responded with status: ${response.status}` };
                } else {
                    dispatch(ActionsDialog.closeDialog());
                    if (props.history && !props.preventNavigation) {
                        props.history.push('/clusters');
                    }
                    return null;
                }
            } catch (err) {
                return { error: 'An unexpected error occurred.' };
            }
        };
        dispatch(
            ActionsDialog.openDialog({
                children: (
                    <DuplicateClusterDialog
                        cluster={cluster}
                        onSave={onSave}
                        onClose={() => dispatch(ActionsDialog.closeDialog())}
                    />
                ),
                maxWidth: 'sm',
                fullWidth: true,
            })
        );
    }
    const handleManageTags = (cluster) => {
        closeSelectedMenu();
        dispatch(
            ActionsDialog.openDialog({
                children: (
                    <ManageTagsDialog
                        cluster={cluster}
                        onClose={() => dispatch(ActionsDialog.closeDialog())}
                    />
                ),
                maxWidth: 'sm',
                fullWidth: true,
            })
        );
    };
    const handlePlan = id => {
        dispatch(Actions.deployCluster(id, true)).catch(err =>
            dispatch(
                ActionsDialog.openDialog({
                    children: <Error message={`${err.data.message}! Refer to logs...`} />,
                    maxWidth: 'lg',
                    fullWidth: true,
                    onClose: (event, reason) => {
                        if (reason !== 'backdropClick') {
                            dispatch(ActionsDialog.closeDialog());
                        }
                    }
                })
            )
        );
        closeSelectedMenu();
    };
    const handleStart = id => {
        // Notify parent about the action (for dashboard map)
        if (props.onActionTriggered) {
            props.onActionTriggered('start', selectedCluster?.name || 'cluster');
        }
        
        dispatch(Actions.startCluster(id)).catch(err =>
            dispatch(
                ActionsDialog.openDialog({
                    children: <Error message={`${err.data.message}! Refer to logs...`} />,
                    maxWidth: 'lg',
                    fullWidth: true,
                    onClose: (event, reason) => {
                        if (reason !== 'backdropClick') {
                            dispatch(ActionsDialog.closeDialog());
                        }
                    }
                })
            )
        );
        setData(data);

        closeSelectedMenu();
        dispatch(ActionsDialog.closeDialog());
    };

    const handleLogs = cluster => {
        const type = 'master';
        dispatch(
            Actions.getLogs({
                id: cluster.id,
                type
            })
        )
            .then(() => showLogs(cluster, type))
            .catch(err =>
                dispatch(
                    ActionsDialog.openDialog({
                        children: <Error message={`${err.data.message}! Refer to logs...`} />,
                        maxWidth: 'lg',
                        fullWidth: true,
                        onClose: (event, reason) => {
                            if (reason !== 'backdropClick') {
                                dispatch(ActionsDialog.closeDialog());
                            }
                        }
                    })
                )
            );
        closeSelectedMenu();
        dispatch(ActionsDialog.closeDialog());
    };
    const saveConfig = cluster => {
        const exportCluster = { ...cluster };
        delete exportCluster?.user;
        delete exportCluster?.data?.keys;
        delete exportCluster?.id;
        delete exportCluster?.updatedAt;
        delete exportCluster?.createdAt;

        downloadFile({
            data: JSON.stringify(exportCluster),
            fileName: exportCluster.name + '.json',
            fileType: 'text/json'
        });
        closeSelectedMenu();
    };
    const showLogs = (cluster, type) => {
        dispatch(
            ActionsDialog.openDialog({
                children: <Logs cluster={cluster} type={type} />,
                maxWidth: 'lg',
                fullWidth: true,
                onClose: (event, reason) => {
                    if (reason !== 'backdropClick') {
                        dispatch(ActionsDialog.closeDialog());
                    }
                }
            })
        );
    };
const manageFastSnapshotRestore = (action, cluster) => {
    dispatch(Actions.manageFastSnapshotRestore(cluster.id, action)).catch(err =>
        dispatch(
            ActionsDialog.openDialog({
                children: <Error message={`${err.data.message}! Refer to logs...`} />,
                maxWidth: 'lg',
                fullWidth: true,
                onClose: (event, reason) => {
                    if (reason !== 'backdropClick') {
                        dispatch(ActionsDialog.closeDialog());
                    }
                }
            })
        )
    );
    closeSelectedMenu();
    dispatch(ActionsDialog.closeDialog());
};

    const showClusterActionConfirmation = async (selectedCluster, action) => {
        closeSelectedMenu();
        const { state } = selectedCluster.info;
        const message = getClusterActionConfirmationMessage(state, action);

        const STATE_LABELS = {
            stop: 'Stop',
            destroy: 'Destroy',
            recycle: 'Recycle',
            resetState: 'Reset State',
            delete: 'Delete'
        }

        // Cluster delete with recordings: divert to a richer confirmation
        // dialog that lets the user move recordings to another cluster
        // before the cluster goes away. Skip when no recordings exist or
        // the lookup fails (best-effort - falls through to the normal
        // confirm path so a backend hiccup doesn't block delete).
        if (action === 'delete') {
            try {
                const recResp = await dispatch(RecordingActions.getClusterRecordings(selectedCluster.id));
                const recs = (recResp && recResp.recordings) || [];
                if (recs.length > 0) {
                    dispatch(
                        ActionsDialog.openDialog({
                            children: (
                                <DeleteClusterWithRecordingsDialog
                                    cluster={selectedCluster}
                                    recordings={recs}
                                    onCancel={() => dispatch(ActionsDialog.closeDialog())}
                                    onConfirm={() => {
                                        dispatch(ActionsDialog.closeDialog());
                                        deleteSelected(selectedCluster.id);
                                    }}
                                />
                            ),
                            maxWidth: 'sm',
                            fullWidth: true,
                            disableRestoreFocus: true,
                            onClose: (_, reason) => {
                                if (reason !== 'backdropClick') {
                                    dispatch(ActionsDialog.closeDialog());
                                }
                            }
                        })
                    );
                    return;
                }
            } catch (e) {
                // fall through to standard confirm
            }
        }

        if (action === 'destroy' || action === 'recycle' || action === 'delete') {
            const handleAction = () => {
                if (action === 'destroy') handleDestroy(selectedCluster.id);
                else if (action === 'recycle') handleRecycle(selectedCluster.id);
                else deleteSelected(selectedCluster.id);
            };
            dispatch(
                ActionsDialog.openDialog({
                    children: (
                        <DestroyConfirmContent
                            clusterName={selectedCluster.name}
                            clusterDisplayName={selectedCluster.displayName}
                            action={action}
                            message={message}
                            onConfirm={handleAction}
                            onCancel={() => dispatch(ActionsDialog.closeDialog())}
                        />
                    ),
                    maxWidth: 'sm',
                    fullWidth: true,
                    disableRestoreFocus: true,
                    onClose: (_, reason) => {
                        if (reason !== 'backdropClick') {
                            dispatch(ActionsDialog.closeDialog());
                        }
                    }
                })
            );
            return;
        }

        dispatch(
            ActionsDialog.openDialog({
                children: (
                    <React.Fragment>
                        <DialogTitle>Warning</DialogTitle>
                        <DialogContent>
                            <DialogContentText id="clusters_table_confirmation-msg">
                                {message}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                id="clusters_table_confirmation-back-btn"
                                onClick={() => dispatch(ActionsDialog.closeDialog())}
                                color="primary"
                            >
                                Back
                            </Button>
                            <Button
                                id="clusters_table_confirmation-confirm-btn"
                                onClick={() => {
                                    switch (action) {
                                        case 'stop':
                                            handleStop(selectedCluster.id);
                                            return
                                        case 'destroy':
                                            handleDestroy(selectedCluster.id);
                                            return
                                        case 'recycle':
                                            handleRecycle(selectedCluster.id);
                                            return
                                        case 'resetState':
                                            handleResetState(selectedCluster.id);
                                            return
                                        case 'delete':
                                            deleteSelected(selectedCluster.id);
                                            return
                                        default:
                                            console.warn(`Unknown action: ${action}`);
                                            return;
                                    }
                                }}
                                variant="contained"
                                autoFocus
                                style={{
                                    backgroundColor: '#F44336',
                                    color: '#fff',
                                }}
                            >
                                {STATE_LABELS[action]}
                            </Button>
                        </DialogActions>
                    </React.Fragment>
                ),
                maxWidth: 'sm',
                fullWidth: true,
                disableRestoreFocus: true,
                onClose: (_, reason) => {
                    if (reason !== 'backdropClick') {
                        dispatch(ActionsDialog.closeDialog());
                    }
                }
            })
        );
    };

    const handleEditCluster = () => {
        closeSelectedMenu();
        const dialogConfig = (clusterForm) => ({
            children: clusterForm,
            maxWidth: 'lg',
            fullWidth: true,
            onClose: (_, reason) => {
                if (reason !== 'backdropClick') {
                    dispatch(ActionsDialog.closeDialog());
                }
            }
        });

        const clusterFormComponents = {
            [PROVIDERS.AWS]: <AwsCluster history={props.history} type={CLUSTER_FORM_TYPE.EDIT} />,
            [PROVIDERS.OCI]: <OracleCluster history={props.history} type={CLUSTER_FORM_TYPE.EDIT} />,
            [PROVIDERS.MAAS]: <MaasCluster history={props.history} type={CLUSTER_FORM_TYPE.EDIT} />,
            [PROVIDERS.AZURE]: <AzureCluster history={props.history} type={CLUSTER_FORM_TYPE.EDIT} />,
            [PROVIDERS.GCP]: <GcpCluster history={props.history} type={CLUSTER_FORM_TYPE.EDIT} />,
            [PROVIDERS.IBM]: <IbmCluster history={props.history} type={CLUSTER_FORM_TYPE.EDIT} />
        };

        dispatch(ClusterActions.getCluster({ id: selectedCluster.id })).then(() => {
            dispatch(ActionsDialog.closeDialog());
            const clusterForm = clusterFormComponents[selectedCluster.provider];
            dispatch(ActionsDialog.openDialog(dialogConfig(clusterForm)));
        });
    };

    const handleEditCollaborators = (selected) => {
        closeSelectedMenu();
        if (selected.info.state === 'deploying' || selected.info.state === 'destroying') {
            dispatch(
                ActionsDialog.openDialog({
                    children: <Error message={`Cluster is undergoing an action.`} />,
                    maxWidth: 'md',
                    fullWidth: true,
                    onClose: (event, reason) => {
                        if (reason !== 'backdropClick') {
                            dispatch(ActionsDialog.closeDialog());
                        }
                    }
                })
            )
        } else {
            dispatch(
                ActionsDialog.openDialog({
                    children: (
                        <Collaborators
                            history={props.history}
                            resource={{
                                data: { ...selected },
                                type: 'cluster'
                            }}
                            actions={{
                                put: Actions.editCollaborators
                            }}
                        />
                    ),
                    maxWidth: 'sm',
                    fullWidth: true,
                    onClose: (event, reason) => {
                        if (reason !== 'backdropClick') {
                            dispatch(ActionsDialog.closeDialog());
                        }
                    }
                })
            );
        }
    };
    const downloadFile = ({ data, fileName, fileType }) => {
        const blob = new Blob([data], { type: fileType });
        const a = document.createElement('a');
        a.download = fileName;
        a.href = window.URL.createObjectURL(blob);
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true
        });
        a.dispatchEvent(clickEvt);
        a.remove();
    };
    const handleResetState = id => {
        dispatch(Actions.forceDestroyCluster(id, true)).catch(err =>
            dispatch(
                ActionsDialog.openDialog({
                    children: <Error message={`${err.data.message}! Refer to logs...`} />,
                    maxWidth: 'lg',
                    fullWidth: true,
                    onClose: (event, reason) => {
                        if (reason !== 'backdropClick') {
                            dispatch(ActionsDialog.closeDialog());
                        }
                    }
                })
            )
        );
        setData(data);

        closeSelectedMenu();
        dispatch(ActionsDialog.closeDialog());
    };
    const handleDestroy = id => {
        // Close the menu immediately
        closeSelectedMenu();
        
        // Notify parent about the action (for dashboard map)
        if (props.onActionTriggered) {
            props.onActionTriggered('destroy', selectedCluster?.name || 'cluster');
        }
        
        // Navigate back to cluster table immediately when destroy is initiated
        // Skip navigation if preventNavigation prop is set (e.g., from dashboard dialog)
        if (props.history && !props.preventNavigation) {
            props.history.push('/clusters');
        }
        
        // Dispatch the destroy action
        dispatch(Actions.destroyCluster(id))
            .catch(err =>
                dispatch(
                    ActionsDialog.openDialog({
                        children: <Error message={`${err.data.message}! Refer to logs...`} />,
                        maxWidth: 'lg',
                        fullWidth: true,
                        onClose: (event, reason) => {
                            if (reason !== 'backdropClick') {
                                dispatch(ActionsDialog.closeDialog());
                            }
                        }
                    })
                )
            );
        setData(data);
        dispatch(ActionsDialog.closeDialog());
    };

    const handleRecycle = async (id) => {
        // Close the menu immediately
        closeSelectedMenu();
        
        // Notify parent about the action (for dashboard map)
        if (props.onActionTriggered) {
            props.onActionTriggered('recycle', selectedCluster?.name || 'cluster');
        }
        
        // Navigate back to cluster table immediately when recycle is initiated
        // Skip navigation if preventNavigation prop is set (e.g., from dashboard dialog)
        if (props.history && !props.preventNavigation) {
            props.history.push('/clusters');
        }

        try {
            // First set the recycling flag
            const recycleActionData = {
                clusterId: id,
                action: 'set-recycling-flag',
                config: {}
            };
            
            await dispatch(SettingsActions.executeDeveloperClusterAction(recycleActionData));
            
            // Then destroy the cluster - deployment will happen automatically after destroy completes
            dispatch(Actions.destroyCluster(id))
                .catch(err =>
                    dispatch(
                        ActionsDialog.openDialog({
                            children: <Error message={`${err.data.message}! Refer to logs...`} />,
                            maxWidth: 'lg',
                            fullWidth: true,
                            onClose: (event, reason) => {
                                if (reason !== 'backdropClick') {
                                    dispatch(ActionsDialog.closeDialog());
                                }
                            }
                        })
                    )
                );
        } catch (err) {
            console.error('Error setting recycle flag:', err);
            dispatch(
                ActionsDialog.openDialog({
                    children: <Error message={`Failed to set recycle flag: ${err.message}`} />,
                    maxWidth: 'lg',
                    fullWidth: true,
                    onClose: (event, reason) => {
                        if (reason !== 'backdropClick') {
                            dispatch(ActionsDialog.closeDialog());
                        }
                    }
                })
            );
        }
        
        setData(data);
        dispatch(ActionsDialog.closeDialog());
    };

    const handleStop = async (id) => {
        dispatch(ActionsDialog.closeDialog());
        
        // Notify parent about the action (for dashboard map)
        if (props.onActionTriggered) {
            props.onActionTriggered('stop', selectedCluster?.name || 'cluster');
        }

        try {
            dispatch(Actions.stopCluster(id));
            setData(data);
            closeSelectedMenu();
        } catch (err) {
            console.error(err)
        }

        dispatch(ActionsDialog.closeDialog());
    };
    const deployConfirm = id => {
        dispatch(ActionsDialog.closeDialog());

        // Notify parent about the action (for dashboard map)
        if (props.onActionTriggered) {
            props.onActionTriggered('deploy', selectedCluster?.name || 'cluster');
        }

        if (props.onDeploy) {
            props.onDeploy(id);
        } else {
            dispatch(Actions.deployCluster(id)).catch(err =>
                dispatch(
                    ActionsDialog.openDialog({
                        children: <Error message={`${err.data.message}! Refer to logs...`} />,
                        maxWidth: 'lg',
                        fullWidth: true,
                        onClose: (event, reason) => {
                            if (reason !== 'backdropClick') {
                                dispatch(ActionsDialog.closeDialog());
                            }
                        }
                    })
                )
            );
        }
        closeSelectedMenu();
        setData(data);
    };
    const filterClusterAction = (cluster) => {
        const isEditable = cluster &&
            (cluster.info.state === CLUSTER_STATES.DESTROYED || 
             cluster.info.state === CLUSTER_STATES.FAILED);

        return {
            icon: isEditable ? "edit" : "visibility",
            label: isEditable ? "Edit" : "View Configuration"
        };
    };
    const deleteSelected = id => {
        // Close the menu immediately
        closeSelectedMenu();
        dispatch(ActionsDialog.closeDialog());
        
        // Navigate back to cluster table immediately when delete is initiated
        // Skip navigation if preventNavigation prop is set (e.g., from dashboard dialog)
        if (props.history && !props.preventNavigation) {
            props.history.push('/clusters');
        }
        
        // Dispatch the delete action
        dispatch(Actions.deleteCluster(id));
    };
    // Guard against a stale anchor element that's been detached from the DOM
    // (e.g. after the table re-renders). Without this, MUI positions the menu at (0,0).
    const anchorEl = props.selectedMenu;
    const isAnchorAttached = anchorEl && document.body.contains(anchorEl);

    return (
        <Menu
            id="clusters_table_selectedMenu"
            key={`selectedMenu`}
            anchorEl={isAnchorAttached ? anchorEl : null}
            open={Boolean(isAnchorAttached)}
            onClose={closeSelectedMenu}
            PaperProps={{
                className: menuPaperClass
            }}
            getContentAnchorEl={null}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <MenuList>
            {((role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR))) && (
                    <MenuItem
                        key={`edit`}
                        id="clusters_table_edit"
                        onClick={() => handleEditCluster(selectedCluster)}
                    >
                        <ListItemIcon className="min-w-40">
                            <Icon>{filterClusterAction(selectedCluster).icon}</Icon>
                        </ListItemIcon>
                        <ListItemText id="clusters_table_deploy-text-text" primary={filterClusterAction(selectedCluster).label} />
                    </MenuItem>
                )}
                {selectedCluster && licenseType === 'Site' && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && isUserOwner(selectedCluster.owner) && (
                    <MenuItem
                        disabled={selectedCluster.info.state === CLUSTER_STATES.DEPLOYING || selectedCluster.info.state === CLUSTER_STATES.DESTROYING}
                        key={`edit-collab`}
                        id="clusters_table_edit-collab"
                        onClick={() => handleEditCollaborators(selectedCluster)}
                    >
                        <ListItemIcon className="min-w-40">
                            <Icon>share</Icon>
                        </ListItemIcon>
                        <ListItemText id="clusters_table_deploy-text-text" primary="Edit Collaborators" />
                    </MenuItem>
                )}

                {selectedCluster && selectedCluster.info.state === CLUSTER_STATES.DESTROYED && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (role.includes('super-admin') || isUserOwner(selectedCluster.owner)) && (
                    <MenuItem
                        id="clusters_table_deploy"
                        key={`deploy`}
                        disabled={selectedCluster.info.bursting.type !== 'none'}
                        onClick={() => handleDeploy(selectedCluster.id)}
                    >
                        <ListItemIcon className="min-w-40">
                            <Icon>cloud_upload</Icon>
                        </ListItemIcon>
                        <ListItemText id="clusters_table_deploy-text-text" primary="Deploy" />
                    </MenuItem>
                )}
                {selectedCluster &&
                    ['aws', 'gcp', 'azure'].includes(selectedCluster.provider) &&
                    selectedCluster.deviceType !== 'commissioned' &&
                    [CLUSTER_STATES.DEPLOYED].includes(selectedCluster.info.state) && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (
                        <MenuItem
                            id="stop"
                            key={`stop`}
                            disabled={selectedCluster.info.bursting.type !== 'none' || selectedCluster.info.state === 'destroying'}
                            onClick={() => showClusterActionConfirmation(selectedCluster, 'stop')}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>cloud_download</Icon>
                            </ListItemIcon>
                            <ListItemText id="stop-text" primary="Stop" />
                        </MenuItem>
                    )}
                {selectedCluster &&
                    selectedCluster.info.state === CLUSTER_STATES.STOPPED &&
                    selectedCluster.deviceType !== 'commissioned' &&
                    (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (
                        <MenuItem
                            id="start"
                            key={`start`}
                            disabled={selectedCluster.info.bursting.type !== 'none'}
                            onClick={() => handleStart(selectedCluster.id)}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>cloud_upload</Icon>
                            </ListItemIcon>
                            <ListItemText id="start-text" primary="Start" />
                        </MenuItem>
                    )}
                {selectedCluster &&
                    selectedCluster.info.state !== CLUSTER_STATES.DESTROYING &&
                    selectedCluster.info.state !== CLUSTER_STATES.DESTROYED && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (role.includes('super-admin') || isUserOwner(selectedCluster.owner)) && (
                        <MenuItem
                            id="clusters_table_destroy"
                            key={`destroy`}
                            disabled={selectedCluster.info.bursting.type !== 'none' || selectedCluster.locks?.destroyLock?.active}
                            onClick={() => showClusterActionConfirmation(selectedCluster, 'destroy')}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>cloud_download</Icon>
                            </ListItemIcon>
                            <ListItemText id="clusters_table_destroy-text" primary={selectedCluster.info.state !== CLUSTER_STATES.DEPLOYING ? "Destroy" : "Force Destroy"} />
                        </MenuItem>
                    )}
                {selectedCluster &&
                    selectedCluster.info.state !== CLUSTER_STATES.DESTROYING &&
                    selectedCluster.info.state !== CLUSTER_STATES.DESTROYED && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (role.includes('super-admin') || isUserOwner(selectedCluster.owner)) && (
                        <MenuItem
                            id="clusters_table_recycle"
                            key={`recycle`}
                            disabled={selectedCluster.info.bursting.type !== 'none' || selectedCluster.locks?.destroyLock?.active}
                            onClick={() => showClusterActionConfirmation(selectedCluster, 'recycle')}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>autorenew</Icon>
                            </ListItemIcon>
                            <ListItemText id="clusters_table_recycle-text" primary="Recycle" />
                        </MenuItem>
                    )}
                {selectedCluster && selectedCluster.provider === 'maas' &&
                    selectedCluster.info.state === CLUSTER_STATES.FAILED && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (
                        <MenuItem
                            id="clusters_table_reset_state"
                            key={`reset`}
                            onClick={() => showClusterActionConfirmation(selectedCluster, 'resetState')}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>cloud_download</Icon>
                            </ListItemIcon>
                            <ListItemText id="clusters_table_destroy-text" primary="Reset State" />
                        </MenuItem>
                    )}

                {selectedCluster && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && [
                    <MenuItem
                        id="clusters_table_logs"
                        key="logs"
                        onClick={() => handleLogs(selectedCluster)}
                    >
                        <ListItemIcon className="min-w-40">
                            <Icon>list</Icon>
                        </ListItemIcon>
                        <ListItemText id="clusters_table_logs-text" primary="Logs" />
                    </MenuItem>,
                    <MenuItem
                        id="clusters_table_save-config"
                        key="save-config"
                        onClick={() => saveConfig(selectedCluster)}
                    >
                        <ListItemIcon className="min-w-40">
                            <Icon>get_app</Icon>
                        </ListItemIcon>
                        <ListItemText id="clusters_table_save-config-text" primary="Save Config" />
                    </MenuItem>
                ]}

                {selectedCluster &&
                    selectedCluster.info.state !== CLUSTER_STATES.DEPLOYING &&
                    selectedCluster.info.state !== CLUSTER_STATES.DESTROYING && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && isUserOwner(selectedCluster.owner) && (
                        <MenuItem
                            key={`delete-1`}
                            id="clusters_table_delete"
                            onClick={() => handlePlan(selectedCluster.id)}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>assignment</Icon>
                            </ListItemIcon>
                            <ListItemText id="clusters_table_delete-text" primary="Plan" />
                        </MenuItem>
                    )}

                {selectedCluster &&
                    (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (!selectedCluster.info.isMarketplace) &&
                    selectedCluster.provider === PROVIDERS.AWS &&
                    isUserOwner(selectedCluster.owner) && (
                        <MenuItem
                            disabled={selectedCluster.info.state === CLUSTER_STATES.DEPLOYING || selectedCluster.info.state === CLUSTER_STATES.DESTROYING}
                            id="clusters_table_fsr_toggle"
                            key="fsr_toggle"
                            onClick={() => {
                                manageFastSnapshotRestore(
                                    selectedCluster.info.fsr_state === FSR_STATE.ENABLED
                                        ? FSR_ACTIONS.DISABLE
                                        : FSR_ACTIONS.ENABLE,
                                    selectedCluster
                                );
                            }}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>{selectedCluster.info.fsr_state === FSR_STATE.ENABLED ? "sync_disabled" : "sync"}</Icon>
                            </ListItemIcon>
                            <ListItemText
                                id="clusters_table_fsr_toggle-text"
                                primary={selectedCluster.info.fsr_state === FSR_STATE.ENABLED ? "Disable FSR" : "Enable FSR"}
                            />
                        </MenuItem>
                    )}

                    <MenuItem
                    id="clusters_table_duplicate_cluster"
                    key={`duplicate_cluster`}
                    onClick={() => {
                           handleDuplicateCluster(selectedCluster);
                    }}
                >
                    <ListItemIcon className="min-w-40">
                        <Icon>file_copy</Icon>
                    </ListItemIcon>
                    <ListItemText id="clusters_table_duplicate_cluster-text" primary="Duplicate Cluster" />
                </MenuItem>
                {selectedCluster && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (
                    <MenuItem
                        id="clusters_table_manage_tags"
                        key="manage_tags"
                        onClick={() => handleManageTags(selectedCluster)}
                    >
                        <ListItemIcon className="min-w-40">
                            <Icon>label</Icon>
                        </ListItemIcon>
                        <ListItemText primary="Manage Tags" />
                    </MenuItem>
                )}
                {selectedCluster && selectedCluster.info.state === CLUSTER_STATES.DEPLOYED && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (
                    <MenuItem
                        key={`dlssh`}
                        id="clusters_table_dlssh"
                        onClick={() => {
                            closeSelectedMenu();
                            dispatch(Actions.downloadSSHKey(selectedCluster));
                        }}
                    >
                        <ListItemIcon className="min-w-40">
                            <Icon>save_alt</Icon>
                        </ListItemIcon>
                        <ListItemText id="clusters_table_dlssh-text" primary="Cluster SSH Key" />
                    </MenuItem>
                )}
                {selectedCluster &&
                    [CLUSTER_STATES.DESTROYED, CLUSTER_STATES.FAILED].includes(selectedCluster.info.state) && (role.includes(USER_ROLE.ADMIN) || role.includes(USER_ROLE.INSTRUCTOR)) && (role.includes('super-admin') || isUserOwner(selectedCluster.owner)) && (
                        <MenuItem
                            key={`delete`}
                            onClick={() => showClusterActionConfirmation(selectedCluster, 'delete')}
                        >
                            <ListItemIcon className="min-w-40">
                                <Icon>delete</Icon>
                            </ListItemIcon>
                            <ListItemText key={`delete-text`} id="clusters_table_delete-text" primary="Delete" />
                        </MenuItem>
                    )}
            </MenuList>
        </Menu>
    )
}